//frontend/src/components/consumers/MyConsumerInfo.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";
import { formatPhone } from "../../utils/formatPhone";
import ConsumerNotes from "./ConsumerNotes";

const MyConsumerInfo = ({ setShowDetails = () => {}, consumerId }) => {
  const { consumer_id } = useParams();
  const navigate = useNavigate();
  const apiBase = getApiBaseURL();
  const { user, tenant_id } = useContext(AuthContext);

  const [info, setInfo] = useState(null);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [showEmploymentFields, setShowEmploymentFields] = useState(false);

  // Role-based restrictions - kept for potential future use
  const isJobCoach = user?.role?.toLowerCase() === "job coach";

  // Calculate age from date of birth
  const calculateAgeFromDOB = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age.toString() : "";
  };

  // Load states
  useEffect(() => {
    const loadStates = async () => {
      setStatesLoading(true);
      try {
        const response = await secureAxios.get(`${apiBase}/api/states`);
        if (Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (err) {
        console.error("Failed to load states:", err);
      } finally {
        setStatesLoading(false);
      }
    };
    loadStates();
  }, [apiBase]);

  // Load consumer info
  useEffect(() => {
    const fetchInfo = async () => {
      if (!consumer_id || !user?.user_id || !tenant_id) {
        return;
      }

      try {
        const params = {
          user_id: user.user_id,
          tenant_id,
          role: user.role,
          tenant_type: user.tenant_type,
        };

        const response = await secureAxios.get(
          `${apiBase}/api/my-consumers/${consumer_id}/info`,
          { params }
        );

        const data = response.data;
        setInfo(data);

        // Process form data similar to EditConsumerForm
        const dobValue = data.date_of_birth
          ? data.date_of_birth.substring(0, 10)
          : "";
        const ageValue = dobValue
          ? calculateAgeFromDOB(dobValue)
          : data.age || "";

        const processedData = {
          consumer_id: data.consumer_id,
          name: data.name || "",
          email: data.email || "",
          phone_number: formatPhone(data.phone_number || ""),
          counselor_id: data.counselor_id || "",
          status_id: data.status_id || "",
          referred_by: data.referred_by || "",
          case_id: data.case_id || "",
          date_of_birth: dobValue,
          notes: data.notes || "",
          address_line_1: data.address_line_1 || "",
          address_line_2: data.address_line_2 || "",
          city: data.city || "",
          state_id: data.state_id || "",
          postal_code: data.postal_code || "",
          emergency_contact_name: data.emergency_contact_name || "",
          emergency_contact_phone: formatPhone(
            data.emergency_contact_phone || ""
          ),
          // Demographics
          age: ageValue,
          sex: data.sex || "",
          marital_status: data.marital_status || "",
          // Employment Assessment Fields
          family_situation: data.family_situation || "",
          education: data.education || "",
          transportation: data.transportation || "",
          work_history: data.work_history || "",
          positive_work_traits: data.positive_work_traits || "",
          negative_work_traits: data.negative_work_traits || "",
          job_preference: data.job_preference || "",
          salary_needs: data.salary_needs || "",
          benefits_needed: data.benefits_needed || "",
          restrictions: data.restrictions || "",
          // HIPAA
          hipaa_consent_date: data.hipaa_consent_date || null,
          data_sharing_consent: data.data_sharing_consent || false,
        };

        setFormData(processedData);
        setOriginalData(processedData);

        // Auto-expand employment fields if data exists
        const hasEmploymentData =
          data.family_situation ||
          data.education ||
          data.transportation ||
          data.work_history ||
          data.positive_work_traits ||
          data.negative_work_traits ||
          data.job_preference ||
          data.salary_needs ||
          data.benefits_needed ||
          data.restrictions;
        setShowEmploymentFields(false);

        await auditApi.logActionSafe(
          "VIEW_CONSUMER_DETAILS",
          "CONSUMER",
          consumer_id,
          {
            consumer_name: data.name,
            fields_accessed: ["full_record"],
            contains_phi: true,
          }
        );
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error(
            "Access denied: You don't have permission to view this consumer's information"
          );
        } else if (err.response?.status === 404) {
          toast.error("Consumer not found");
        } else {
          toast.error("Failed to load consumer information");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [consumer_id, apiBase, user, tenant_id]);

  const handleChange = (e) => {
    if (!e?.target) return;
    const { name, value } = e.target;

    // Special handling for date_of_birth - recalculate age
    if (name === "date_of_birth") {
      const newAge = value ? calculateAgeFromDOB(value) : "";
      setFormData((prev) => ({ ...prev, [name]: value, age: newAge }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setHasChanges(true);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const hasAddressInfo =
      formData.address_line_1 || formData.city || formData.postal_code;

    if (hasAddressInfo && !formData.state_id) {
      newErrors.state_id =
        "State is required when address information is provided";
    }

    if (formData.email && !formData.email.includes("@")) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (!validateForm()) {
        toast.error("Please fix the validation errors before saving");
        return;
      }

      setSaving(true);

      const changedFields = [];
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== originalData[key]) {
          changedFields.push(key);
        }
      });

      await secureAxios.put(
        `${apiBase}/api/my-consumers/${consumer_id}`,
        formData,
        {
          params: {
            tenant_id,
            user_id: user.user_id,
            role: user.role,
            tenant_type: user.tenant_type,
          },
        }
      );

      await auditApi.logActionSafe(
        "UPDATE_CONSUMER_INFO",
        "CONSUMER",
        consumer_id,
        {
          consumer_name: formData.name,
          changed_fields: changedFields,
          contains_phi: true,
        }
      );

      toast.success("Consumer information updated successfully");
      setOriginalData({ ...formData });
      setHasChanges(false);
    } catch (err) {
      console.error("Error updating consumer:", err);
      if (err.response?.status === 403) {
        toast.error(
          "Access denied: You don't have permission to update this consumer"
        );
      } else {
        toast.error("Failed to update consumer information");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate("/crm/my-consumers");
      }
    } else {
      navigate("/crm/my-consumers");
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading consumer information...</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="container mt-4">
        <div className="card card-body shadow-sm">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-exclamation-triangle"></i> Consumer not found or
            access denied.
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/crm/my-consumers")}
          >
            ← Back to My Consumers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          <i className="bi bi-person-lines-fill me-2"></i>Consumer Information
        </h4>
        <button
          className="btn btn-link btn-sm"
          onClick={() => navigate("/crm/my-consumers")}
        >
          ← Back to My Consumers
        </button>
      </div>

      {/* PHI Access Notice */}
      <div className="alert alert-warning mb-3">
        <i className="bi bi-shield-lock me-2"></i>
        <strong>Protected Health Information (PHI)</strong> - All access is
        logged for HIPAA compliance.
        {isJobCoach && (
          <small className="d-block mt-1">
            <i className="bi bi-info-circle me-1"></i>
            Some fields are read-only based on your role.
          </small>
        )}
      </div>

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Basic Information */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Basic Information</h5>
        </div>

        <div className="col-md-4">
          <label className="form-label">Name</label>
          <input
            name="name"
            className="form-control"
            value={formData.name || ""}
            readOnly
            disabled
            style={{ backgroundColor: "#e9ecef" }}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            className={`form-control ${errors.email ? "is-invalid" : ""}`}
            value={formData.email || ""}
            onChange={handleChange}
            disabled={saving}
          />
          {errors.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            name="phone_number"
            className="form-control"
            value={formData.phone_number || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        {/* Demographics Row */}
        <div className="col-md-2">
          <label className="form-label">Sex</label>
          <select
            className="form-select"
            name="sex"
            value={formData.sex || ""}
            onChange={handleChange}
            disabled={saving}
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">Marital Status</label>
          <select
            className="form-select"
            name="marital_status"
            value={formData.marital_status || ""}
            onChange={handleChange}
            disabled={saving}
          >
            <option value="">Select</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
            <option value="Widowed">Widowed</option>
            <option value="Separated">Separated</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">
            Date of Birth
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </label>
          <input
            type="date"
            name="date_of_birth"
            className="form-control"
            value={formData.date_of_birth || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Age</label>
          <input
            className="form-control"
            type="text"
            name="age"
            value={formData.age || ""}
            onChange={handleChange}
            placeholder="—"
            disabled={saving || !!formData.date_of_birth}
            readOnly={!!formData.date_of_birth}
            style={formData.date_of_birth ? { backgroundColor: "#e9ecef" } : {}}
          />
          {formData.date_of_birth && (
            <small className="text-muted">Auto-calculated</small>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Case ID</label>
          <input
            name="case_id"
            className="form-control"
            value={formData.case_id || ""}
            readOnly
            disabled
            style={{ backgroundColor: "#e9ecef" }}
          />
        </div>

        {/* Status, Counselor, Referred By - Read Only */}
        <div className="col-md-4">
          <label className="form-label">Status</label>
          <input
            name="status_name"
            className="form-control"
            value={info?.status_name || ""}
            readOnly
            disabled
            style={{ backgroundColor: "#e9ecef" }}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Counselor</label>
          <input
            name="counselor_name"
            className="form-control"
            value={info?.counselor_name || ""}
            readOnly
            disabled
            style={{ backgroundColor: "#e9ecef" }}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Referred By</label>
          <input
            name="referred_by"
            className="form-control"
            value={formData.referred_by || ""}
            readOnly
            disabled
            style={{ backgroundColor: "#e9ecef" }}
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">
            Address Information
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </h5>
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Used for mileage log calculations
          </small>
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            name="address_line_1"
            className="form-control"
            value={formData.address_line_1 || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            name="address_line_2"
            className="form-control"
            value={formData.address_line_2 || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            name="city"
            className="form-control"
            value={formData.city || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            name="state_id"
            className={`form-select ${errors.state_id ? "is-invalid" : ""}`}
            value={formData.state_id || ""}
            onChange={handleChange}
            disabled={saving || statesLoading}
          >
            <option value="">
              {statesLoading ? "Loading..." : "Select State"}
            </option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
          {errors.state_id && (
            <div className="invalid-feedback">{errors.state_id}</div>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            name="postal_code"
            className="form-control"
            value={formData.postal_code || ""}
            onChange={handleChange}
            maxLength="10"
            disabled={saving}
          />
        </div>

        {/* Emergency Contact */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">Emergency Contact</h5>
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Name</label>
          <input
            name="emergency_contact_name"
            className="form-control"
            value={formData.emergency_contact_name || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Phone</label>
          <input
            type="tel"
            name="emergency_contact_phone"
            className="form-control"
            value={formData.emergency_contact_phone || ""}
            onChange={handleChange}
            disabled={saving}
          />
        </div>

        {/* Employment Assessment Data (Collapsible) */}
        <div className="col-12">
          <div className="d-flex align-items-center mt-3 border-bottom pb-2">
            <h5 className="mb-0 me-2">Employment Assessment Data</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowEmploymentFields(!showEmploymentFields)}
            >
              {showEmploymentFields ? (
                <>
                  <i className="bi bi-chevron-up me-1"></i>Hide
                </>
              ) : (
                <>
                  <i className="bi bi-chevron-down me-1"></i>Show
                </>
              )}
            </button>
            <small className="text-muted ms-2">
              (Can also be filled via CRP-31 form)
            </small>
          </div>
        </div>

        {showEmploymentFields && (
          <>
            <div className="col-md-12">
              <label className="form-label">Family Situation</label>
              <textarea
                className="form-control"
                name="family_situation"
                value={formData.family_situation || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Education</label>
              <textarea
                className="form-control"
                name="education"
                value={formData.education || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Transportation</label>
              <textarea
                className="form-control"
                name="transportation"
                value={formData.transportation || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Restrictions (if any)</label>
              <textarea
                className="form-control"
                name="restrictions"
                value={formData.restrictions || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Work History</label>
              <textarea
                className="form-control"
                name="work_history"
                value={formData.work_history || ""}
                onChange={handleChange}
                rows="3"
                disabled={saving}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Positive Work Traits</label>
              <textarea
                className="form-control"
                name="positive_work_traits"
                value={formData.positive_work_traits || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Negative Work Traits</label>
              <textarea
                className="form-control"
                name="negative_work_traits"
                value={formData.negative_work_traits || ""}
                onChange={handleChange}
                rows="2"
                disabled={saving}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Job Preference</label>
              <input
                className="form-control"
                name="job_preference"
                value={formData.job_preference || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Salary Needs</label>
              <input
                className="form-control"
                name="salary_needs"
                value={formData.salary_needs || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Benefits Needed</label>
              <input
                className="form-control"
                name="benefits_needed"
                value={formData.benefits_needed || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </>
        )}

        {/* Notes */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">
            Setup Notes
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </h5>
        </div>

        <div className="col-12">
          <label className="form-label">General Setup Notes -  <small className="text-muted">Day to Day notes are below.</small> </label>
          <textarea
            name="notes"
            className="form-control"
            value={formData.notes || ""}
            onChange={handleChange}
            rows="3"
            disabled={saving}
          />
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Notes may contain Protected Health Information and are encrypted.
          </small>
        </div>

        {/* HIPAA Consent */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">
            <i className="bi bi-shield-check me-2"></i>HIPAA Consent
          </h5>
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="hipaa_consent"
              checked={!!formData.hipaa_consent_date}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  hipaa_consent_date: e.target.checked
                    ? new Date().toISOString()
                    : null,
                }));
                setHasChanges(true);
              }}
              disabled={saving}
            />
            <label className="form-check-label" htmlFor="hipaa_consent">
              <strong>HIPAA Authorization:</strong> Consumer has provided
              written authorization for the use and disclosure of protected
              health information.
              {formData.hipaa_consent_date && (
                <small className="text-muted ms-2">
                  (Consented:{" "}
                  {new Date(formData.hipaa_consent_date).toLocaleDateString()})
                </small>
              )}
            </label>
          </div>
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="data_sharing_consent"
              checked={formData.data_sharing_consent || false}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  data_sharing_consent: e.target.checked,
                }));
                setHasChanges(true);
              }}
              disabled={saving}
            />
            <label className="form-check-label" htmlFor="data_sharing_consent">
              <strong>Data Sharing Consent:</strong> Consumer consents to
              sharing information with referring agencies and relevant service
              providers.
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="col-12 d-flex justify-content-between mt-4">
          <div>
            <button
              type="submit"
              className="btn btn-primary me-2"
              disabled={saving || !hasChanges || statesLoading}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-1"></i> Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            {hasChanges && !saving && (
              <span className="text-warning ms-3">
                <i className="bi bi-exclamation-circle me-1"></i>You have
                unsaved changes
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Consumer Notes Component - OUTSIDE the form to prevent nested form issues */}
      <div className="mt-4">
        <ConsumerNotes consumerId={consumer_id} consumerName={info?.name} />
      </div>

      {/* Audit Trail */}
      <div className="mt-4 pt-3 border-top">
        <small className="text-muted">
          <i className="bi bi-clock-history me-1"></i>
          {info?.created_at &&
            `Created: ${new Date(info.created_at).toLocaleString()}`}
          {info?.updated_at && (
            <>
              {" | "}
              <i className="bi bi-pencil me-1"></i>
              Last Updated: {new Date(info.updated_at).toLocaleString()}
            </>
          )}
        </small>
      </div>
    </div>
  );
};

export default MyConsumerInfo;
